import { EvaporatorAnimatic, EvaporatorAnimaticProps } from '@crossnokaye/ux-animatics';

import {
  AnimaticEvaporatorConfig,
  AnimaticPrimitiveConfigStatus,
} from '@repo/sdk/models/components';

import {
  ANIMATIC_PRIMARY_LABEL,
  ANIMATIC_SECONDARY_LABEL,
} from '@/features/equipment-browser/animatics/animatics.constants';
import { getLabel } from '@/features/equipment-browser/animatics/animatics.helpers';
import { LabelConfigMap } from '@/features/equipment-browser/animatics/animatics.types';
import { ScaledAnimatic } from '@/features/equipment-browser/animatics/scaled-animatic';
import { getRefValue } from '@/features/equipment-browser/utils';

import { getDefrostConfig, getEvaporatorAnimaticLabels } from './evaporator.utils';
import { AnimaticConfigProps } from './types';

const parseState = (state: string | undefined): EvaporatorAnimaticProps['state'] => {
  if (!state) {
    return 'off';
  }
  switch (state) {
    case AnimaticPrimitiveConfigStatus.Estopped:
      return 'disabled';
    default:
      return state as EvaporatorAnimaticProps['state'];
  }
};

type AnimaticEvaporatorProps = Omit<AnimaticConfigProps, 'state' | 'labels'> & {
  labelConfigs: LabelConfigMap;
};

export const AnimaticEvaporator = ({
  config,
  labelConfigs,
  className,
}: AnimaticEvaporatorProps) => {
  const state = parseState(config.specificAnimaticConfig?.evaporator?.evaporatorState);
  const fanStateRef = config.specificAnimaticConfig?.evaporator?.fanStateRef;
  const fanOn = fanStateRef ? ((getRefValue(fanStateRef) as boolean) ?? false) : false;
  const { defrostPercentage, defrostTickPercentages } = getDefrostConfig(
    config.specificAnimaticConfig?.evaporator as AnimaticEvaporatorConfig
  );

  const labels = getEvaporatorAnimaticLabels(labelConfigs);

  const primaryLabel = getLabel(labels, ANIMATIC_PRIMARY_LABEL)?.text;
  const secondaryLabel = getLabel(labels, ANIMATIC_SECONDARY_LABEL)?.text?.replace(/_/g, ' ');

  return (
    <ScaledAnimatic className={className} scaleX={0.98} scaleY={0.98}>
      <EvaporatorAnimatic
        // If banner label exists, set bottom label to empty string
        bottomLabel={secondaryLabel.toUpperCase()}
        defrostPercentage={defrostPercentage}
        defrostTickPercentages={defrostTickPercentages}
        fanOn={fanOn}
        state={state}
        topLabel={primaryLabel}
      />
    </ScaledAnimatic>
  );
};
